package com.corbettcode.allaboutrocks.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.css.userSelect
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.whiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import kotlin.time.Duration.Companion.milliseconds

/** How long a menu stays open after the mouse leaves it, so a diagonal move to a submenu doesn't close it. */
private val CLOSE_DELAY = 500.milliseconds

/** An entry in a [DropdownMenuComponent]: either something you pick, or a submenu holding more entries. */
sealed interface DropdownEntry {
    val text: String
}

/** A leaf entry. Picking it reports [text] back through `onItemSelected` and closes the whole menu. */
data class DropdownAction(override val text: String) : DropdownEntry

/** An entry that opens a nested panel of [items] when hovered or clicked. Submenus may be nested arbitrarily deep. */
data class DropdownSubMenu(
    override val text: String
    , val items: List<DropdownEntry>
) : DropdownEntry

/** Convenience overload for a flat menu of plain text items. */
@Composable
fun DropdownMenuComponent(
    label: String
    , items: List<String>
    , onItemSelected: (String) -> Unit
    , color: CSSColorValue = Color.white
    , backgroundColor: CSSColorValue = Color("#3d7ebf")
) {
    DropdownMenuComponent(
        label = label
        , entries = items.map { DropdownAction(it) }
        , onItemSelected = onItemSelected
        , color = color
        , backgroundColor = backgroundColor
    )
}

@Composable
fun DropdownMenuComponent(
    label: String
    , entries: List<DropdownEntry>
    , onItemSelected: (String) -> Unit
    , color: CSSColorValue = Color.white
    , backgroundColor: CSSColorValue = Color("#3d7ebf")
) {
    var expanded by remember { mutableStateOf(false) }
    var selectedItem by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()
    var closeJob by remember { mutableStateOf<Job?>(null) }
    // Set when hovering opened the menu, so the click that usually follows doesn't immediately close it again.
    var openedByHover by remember { mutableStateOf(false) }

    // Anchor element that triggers the dropdown. The panel lives inside it, so moving the mouse from the
    // label into the panel (or from a panel into one of its submenus) never triggers a mouse-leave here.
    Div(
        attrs = {
            style {
                position(Position.Relative)
                display(DisplayStyle.Initial)
            }
            onMouseEnter {
                closeJob?.cancel()
                if (!expanded) openedByHover = true
                expanded = true
            }
            onMouseLeave {
                closeJob?.cancel()
                openedByHover = false
                closeJob = scope.launch {
                    delay(CLOSE_DELAY)
                    expanded = false
                }
            }
        }
    ) {

        Row(
            modifier = Modifier
                .color(color)
                .cursor(Cursor.Pointer)
                .userSelect(UserSelect.None)
                .onClick {
                    closeJob?.cancel()
                    if (openedByHover) openedByHover = false else expanded = !expanded
                }
        ) {
            SpanText(selectedItem ?: label)
            SpanText(if (expanded) " ▲" else " ▼")
        }

        if (expanded) {
            DropdownPanel(
                backgroundColor = backgroundColor
                , modifier = Modifier
                    .top(100.percent)
                    .left(0.px)
                    .margin(top = 4.px)
            ) {
                entries.forEach { entry ->
                    DropdownEntryItem(
                        entry = entry
                        , selectedItem = selectedItem
                        , backgroundColor = backgroundColor
                        , onLeafSelected = { text ->
                            selectedItem = text
                            closeJob?.cancel()
                            expanded = false
                            openedByHover = false
                            onItemSelected(text)
                        }
                    )
                }
            }
        }
    }
}

/** The floating panel shared by the root menu and every submenu. [modifier] positions it against its anchor. */
@Composable
private fun DropdownPanel(
    backgroundColor: CSSColorValue
    , modifier: Modifier
    , content: @Composable () -> Unit
) {
    Div(
        attrs = Modifier
            .position(Position.Absolute)
            .minWidth(140.px)
            .backgroundColor(backgroundColor)
            .borderRadius(6.px)
            .border(1.px, LineStyle.Solid, Color("#e0e0e0"))
            .boxShadow(0.px, 4.px, 12.px, color = rgba(0, 0, 0, 0.15))
            .zIndex(100)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .padding(4.px, 0.px)
            .then(modifier)
            .toAttrs()
    ) {
        content()
    }
}

@Composable
private fun DropdownEntryItem(
    entry: DropdownEntry
    , selectedItem: String?
    , backgroundColor: CSSColorValue
    , onLeafSelected: (String) -> Unit
) {
    when (entry) {
        is DropdownAction -> DropdownItem(
            text = entry.text
            , isSelected = entry.text == selectedItem
            , onClick = { onLeafSelected(entry.text) }
        )

        is DropdownSubMenu -> DropdownSubMenuItem(
            subMenu = entry
            , selectedItem = selectedItem
            , backgroundColor = backgroundColor
            , onLeafSelected = onLeafSelected
        )
    }
}

/** A row that opens its own panel to the side, the way nested menus behave elsewhere on the web. */
@Composable
private fun DropdownSubMenuItem(
    subMenu: DropdownSubMenu
    , selectedItem: String?
    , backgroundColor: CSSColorValue
    , onLeafSelected: (String) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()
    var closeJob by remember { mutableStateOf<Job?>(null) }
    // Set when hovering opened the menu, so the click that usually follows doesn't immediately close it again.
    var openedByHover by remember { mutableStateOf(false) }

    Div(
        attrs = {
            style {
                position(Position.Relative)
            }
            onMouseEnter {
                closeJob?.cancel()
                if (!expanded) openedByHover = true
                expanded = true
            }
            onMouseLeave {
                closeJob?.cancel()
                openedByHover = false
                closeJob = scope.launch {
                    delay(CLOSE_DELAY)
                    expanded = false
                }
            }
        }
    ) {
        DropdownItem(
            text = subMenu.text
            // A submenu row is never "the selection" itself, but it highlights while its panel is open.
            , isSelected = false
            , isHighlighted = expanded
            , hasSubMenu = true
            // Click toggles, so the menu is still usable where hovering isn't (touch screens).
            , onClick = {
                closeJob?.cancel()
                if (openedByHover) openedByHover = false else expanded = !expanded
            }
        )

        if (expanded) {
            DropdownPanel(
                backgroundColor = backgroundColor
                , modifier = Modifier
                    .left(100.percent)
                    .top((-4).px)
                    .margin(left = 2.px)
            ) {
                subMenu.items.forEach { item ->
                    DropdownEntryItem(
                        entry = item
                        , selectedItem = selectedItem
                        , backgroundColor = backgroundColor
                        , onLeafSelected = onLeafSelected
                    )
                }
            }
        }
    }
}

@Composable
private fun DropdownItem(
    text: String
    , isSelected: Boolean
    , onClick: () -> Unit
    , isHighlighted: Boolean = false
    , hasSubMenu: Boolean = false
) {
    var isHovered by remember { mutableStateOf(false) }

    val selected = if (ColorMode.current == ColorMode.LIGHT) Color.white else Color.black
    val hovered = if (ColorMode.current == ColorMode.LIGHT) Color.lightgray else Color.darkslategray

    val textColor = if (ColorMode.current == ColorMode.LIGHT) Color.black else Color.ivory
    val textColorSelected = if (ColorMode.current == ColorMode.LIGHT) Color.black else Color.white
    val textColorHovered = if (ColorMode.current == ColorMode.LIGHT) Color.black else Color.white

    val highlighted = isHovered || isHighlighted

    Div(
        attrs = {
            onClick { onClick() }
            onMouseEnter { isHovered = true }
            onMouseLeave { isHovered = false }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                gap(12.px)
                whiteSpace(WhiteSpace.NoWrap)
                padding(10.px, 16.px)
                cursor("pointer")
                fontSize(14.px)
                color(
                    when {
                        isSelected  -> textColorSelected
                        highlighted -> textColorHovered
                        else        -> textColor
                    }
                )
                fontWeight(if (isSelected) "bold" else "normal")
                backgroundColor(
                    when {
                        isSelected  -> selected
                        highlighted -> hovered
                        else        -> Color.transparent
                    }
                )
                userSelect(UserSelect.None)
            }
        }
    ) {
        SpanText(text)
        if (hasSubMenu) {
            SpanText("▸")
        }
    }
}
